import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react'

const Footer = () => {
  return (
    <footer className="relative text-white overflow-hidden">
      {/* Gradient background: mauve / gray / pink mix */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-950/80 via-gray-900 to-pink-950/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-transparent to-pink-900/20" />
      {/* Subtle top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-400/50 to-transparent" />

      {/* Decorative stars */}
      <div className="absolute inset-0 opacity-70 pointer-events-none">
        <div className="absolute top-8 left-8 w-2 h-2 bg-pink-300/90 rounded-full animate-pulse" style={{ animationDuration: '2s' }} />
        <div className="absolute top-16 right-16 w-1.5 h-1.5 bg-purple-300/90 rounded-full animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
        <div className="absolute top-24 left-1/4 w-2.5 h-2.5 bg-white/80 rounded-full animate-pulse" style={{ animationDuration: '1.8s', animationDelay: '1s' }} />
        <div className="absolute top-12 right-1/3 w-1.5 h-1.5 bg-pink-200/80 rounded-full animate-pulse" style={{ animationDuration: '2.2s', animationDelay: '0.3s' }} />
        <div className="absolute top-20 left-1/2 w-2 h-2 bg-purple-200/80 rounded-full animate-pulse" style={{ animationDuration: '2.4s', animationDelay: '0.7s' }} />
        <div className="absolute top-28 right-12 w-1.5 h-1.5 bg-pink-400/70 rounded-full animate-pulse" style={{ animationDuration: '1.6s', animationDelay: '1.2s' }} />
        <div className="absolute top-32 left-20 w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{ animationDuration: '2.8s', animationDelay: '0.2s' }} />
        <div className="absolute top-10 left-2/3 w-1 h-1 bg-purple-300/80 rounded-full animate-pulse" style={{ animationDuration: '2s', animationDelay: '0.9s' }} />
        <div className="absolute bottom-24 left-12 w-2 h-2 bg-pink-300/80 rounded-full animate-pulse" style={{ animationDuration: '2.2s', animationDelay: '0.4s' }} />
        <div className="absolute bottom-16 right-20 w-1.5 h-1.5 bg-purple-200/80 rounded-full animate-pulse" style={{ animationDuration: '1.9s', animationDelay: '0.6s' }} />
        <div className="absolute bottom-20 left-1/3 w-2.5 h-2.5 bg-white/70 rounded-full animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '0.1s' }} />
        <div className="absolute bottom-12 right-1/4 w-1.5 h-1.5 bg-pink-200/70 rounded-full animate-pulse" style={{ animationDuration: '2.1s', animationDelay: '0.8s' }} />
        <div className="absolute bottom-28 left-3/4 w-2 h-2 bg-purple-300/70 rounded-full animate-pulse" style={{ animationDuration: '1.7s', animationDelay: '0.3s' }} />
        <div className="absolute bottom-8 right-1/2 w-1 h-1 bg-white/60 rounded-full animate-pulse" style={{ animationDuration: '2.3s', animationDelay: '0.5s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-12 md:py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center md:text-left">
            {/* Brand */}
            <div className="flex flex-col items-center md:items-start">
              <Link to="/" className="flex items-center gap-3 mb-4 group">
                <img
                  src="/media/images/logo/lumoralogo.png"
                  alt="Spa~Radise Luxe"
                  className="w-12 h-12 object-contain rounded-full border border-white/20 shadow-lg"
                />
                <span className="text-xl font-serif font-semibold text-white group-hover:text-pink-200 transition-colors">
                  Spa~Radise Luxe
                </span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs mb-6">
                A soft, romantic sanctuary for body and soul. Melt into gentle, candlelit calm with bespoke spa rituals tailored just for you.
              </p>
              <div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-3">Follow us</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-4">
                  <a
                    href="https://wa.me/233537813579"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-green-400 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                    title="WhatsApp"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/spa_radise579?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-pink-300 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                    title="Instagram"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781c-.49 0-.98-.49-.98-.98s.49-.98.98-.98.98.49.98.98-.49.98-.98.98z" />
                    </svg>
                  </a>
                  <a
                    href="https://vm.tiktok.com/ZS9eGpJ16woLp-jVb5G/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5"
                    title="TikTok"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61588273271316"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-blue-400 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                    title="Facebook"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22 12.07C22 6.48 17.52 2 11.93 2 6.35 2 1.87 6.48 1.87 12.07c0 4.78 3.44 8.74 7.94 9.64v-6.82H7.43v-2.82h2.38V9.41c0-2.35 1.4-3.65 3.55-3.65 1.03 0 2.1.18 2.1.18v2.31h-1.18c-1.16 0-1.52.72-1.52 1.46v1.76h2.59l-.41 2.82h-2.18v6.82c4.5-.9 7.94-4.86 7.94-9.64z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="flex flex-col items-center md:items-start">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Quick Links</h3>
              <ul className="space-y-3">
                {[
                  { to: '/about', label: 'About' },
                  { to: '/services', label: 'Services' },
                  { to: '/gallery', label: 'Gallery' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/help-center', label: 'Help Center' },
                  { to: '/gift-card', label: 'Gift Card' },
                  { to: '/contact', label: 'Contact' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="text-gray-400 hover:text-pink-200 transition-colors text-sm">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="flex flex-col items-center md:items-start">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</h3>
              <div className="space-y-4">
                <a href="tel:+233202468529" className="flex items-center gap-3 text-gray-400 hover:text-pink-200 transition-colors text-sm">
                  <Phone className="w-4 h-4 text-pink-400/80 flex-shrink-0" />
                  <span>020 246 8529</span>
                </a>
                <a href="https://wa.me/233537813579" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-green-400 transition-colors text-sm">
                  <MessageCircle className="w-4 h-4 text-green-400/80 flex-shrink-0" />
                  <span>053 781 3579 (WhatsApp)</span>
                </a>
                <a href="mailto:sparadiseluxe@gmail.com" className="flex items-center gap-3 text-gray-400 hover:text-pink-200 transition-colors text-sm">
                  <Mail className="w-4 h-4 text-pink-400/80 flex-shrink-0" />
                  <span>sparadiseluxe@gmail.com</span>
                </a>
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>Serving Greater Area</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Spa~Radise Luxe. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-pink-200 text-sm transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-pink-200 text-sm transition-colors">
                Terms
              </Link>
            </div>
          </div>

          {/* Developed by – logo */}
          <div className="flex flex-col items-center justify-center mt-8 pt-6 border-t border-gray-800">
            <p className="text-gray-500 text-xs mb-2">Developed by</p>
            <a
              href="https://kingparasite.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="King Parasite Enterprises"
            >
              <img
                src="/media/images/logo/kp.png"
                alt="Developer logo"
                className="h-8 w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
